'use client';
import React from 'react';
/**
 * Component for rendering text with inline citations.
 *
 * Parses `[source-id]` patterns in text and renders them as superscript
 * links that reference the sources section.
 *
 * ## Why Inline Citations Matter for AEO
 *
 * AI systems (ChatGPT, Google AI Overviews) need to associate
 * claims with their sources. Footer-only citations create no semantic link
 * between "77% of patients..." and the actual study. Inline citations create
 * that direct association, making our content more likely to be cited by AI.
 *
 * ## Usage
 *
 * ```tsx
 * import { CitedText, CitationProvider } from '@/components/citations';
 *
 * // Wrap content in provider with sources
 * <CitationProvider sources={problem.sources} allContent={allContent}>
 *   <CitedText>
 *     Google evaluates healthcare content using E-E-A-T criteria.[google-qrg]
 *   </CitedText>
 * </CitationProvider>
 * ```
 *
 * ## How It Works
 *
 * 1. CitationProvider tracks which sources are referenced and assigns numbers
 * 2. CitedText parses [source-id] patterns and renders superscript links
 * 3. Numbers are assigned in order of first appearance across all CitedText instances
 * 4. Links scroll to the corresponding source in the footer
 *
 * @see {@link SourcesCitation} - Renders the numbered sources footer
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { CitationSource } from './types';

/**
 * Context value for citation tracking.
 */
interface CitationContextValue {
  /** Map of source ID to its assigned citation number */
  sourceNumbers: Map<string, number>;
  /** Full source data keyed by ID */
  sourcesById: Map<string, CitationSource>;
  /** Register a source reference and get its number */
  getSourceNumber: (sourceId: string) => number | null;
}

const CitationContext = createContext<CitationContextValue | null>(null);

/**
 * Hook to access citation context.
 */
export function useCitationContext(): CitationContextValue {
  const context = useContext(CitationContext);
  if (!context) {
    throw new Error('useCitationContext must be used within a CitationProvider');
  }
  return context;
}

interface CitationProviderProps {
  /** Source definitions with IDs */
  sources: CitationSource[];
  /** All text content to scan for citations (intro + all paragraphs) */
  allContent: string[];
  children: ReactNode;
}

/**
 * Provider that tracks citation references across content.
 *
 * Scans all content upfront to assign stable citation numbers based on
 * order of first appearance.
 */
export function CitationProvider({ sources, allContent, children }: CitationProviderProps): React.ReactElement {
  const value = useMemo(() => {
    // Build map of sources by ID
    const sourcesById = new Map<string, CitationSource>();
    for (const source of sources) {
      sourcesById.set(source.id, source);
    }

    // Scan all content to find citation order
    const sourceNumbers = new Map<string, number>();
    let nextNumber = 1;

    // Pattern to match [source-id]
    const citationPattern = /\[([a-z0-9-]+)\]/g;

    // Scan all content in order
    for (const text of allContent) {
      let match;
      while ((match = citationPattern.exec(text)) !== null) {
        const sourceId = match[1];
        // Only assign number if it's a valid source and not yet numbered
        if (sourcesById.has(sourceId) && !sourceNumbers.has(sourceId)) {
          sourceNumbers.set(sourceId, nextNumber++);
        }
      }
    }

    const getSourceNumber = (sourceId: string): number | null => {
      return sourceNumbers.get(sourceId) ?? null;
    };

    return { sourceNumbers, sourcesById, getSourceNumber };
  }, [sources, allContent]);

  return <CitationContext.Provider value={value}>{children}</CitationContext.Provider>;
}

interface CitedTextProps {
  /** Text content that may contain [source-id] citation markers */
  children: string;
  /** Optional className for the container */
  className?: string;
}

/**
 * Renders text with inline citation markers converted to superscript links.
 *
 * @example
 * ```tsx
 * <CitedText className="text-slate-600">
 *   Google evaluates healthcare content using E-E-A-T criteria.[google-qrg]
 * </CitedText>
 * ```
 */
export function CitedText({ children: text, className }: CitedTextProps): React.ReactElement {
  const { getSourceNumber, sourcesById } = useCitationContext();

  // Parse text and replace [source-id] with superscript links
  const elements = useMemo(() => {
    const result: ReactNode[] = [];
    const citationPattern = /\[([a-z0-9-]+)\]/g;

    let lastIndex = 0;
    let match;

    while ((match = citationPattern.exec(text)) !== null) {
      // Add text before the citation
      if (match.index > lastIndex) {
        result.push(text.slice(lastIndex, match.index));
      }

      const sourceId = match[1];
      const number = getSourceNumber(sourceId);
      const source = sourcesById.get(sourceId);

      if (number !== null && source) {
        // Render as superscript link
        result.push(
          <sup key={`${sourceId}-${match.index}`} className="ml-0.5">
            <a
              href={`#ref-${sourceId}`}
              className="text-[#B45309] hover:text-[#92400E] transition-colors font-medium text-[0.7em] no-underline hover:underline"
              title={source.name}
            >
              [{number}]
            </a>
          </sup>
        );
      } else {
        // Unknown source - render as plain text (shouldn't happen with valid data)
        result.push(`[${sourceId}]`);
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last citation
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result;
  }, [text, getSourceNumber, sourcesById]);

  return <span className={className}>{elements}</span>;
}

/**
 * Get the ordered list of sources that were actually cited in content.
 *
 * Returns sources in citation order (order of first appearance).
 */
export function useOrderedSources(): CitationSource[] {
  const { sourceNumbers, sourcesById } = useCitationContext();

  return useMemo(() => {
    // Sort by citation number
    const entries = Array.from(sourceNumbers.entries()).sort((a, b) => a[1] - b[1]);

    // Return sources in order
    return entries
      .map(([id]) => sourcesById.get(id))
      .filter((source): source is CitationSource => source !== undefined);
  }, [sourceNumbers, sourcesById]);
}
